import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSchool } from '../context/SchoolContext';
import { useTheme, CLASS_ROUTE_PREFIXES } from '../context/ThemeContext';
import { FeedbackModal } from './FeedbackModal';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { API_BASE } from '../apiConfig';
import { 
  LayoutDashboard, 
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronLeft,
  Building2,
  Info,
  Calendar,
  Sun,
  Crown,
  Shield,
  MessageSquare,
  Moon,
  Monitor,
  Radio,
  Sparkles,
  Wallet,
  Clock,
  Mail,
  FolderOpen
} from 'lucide-react';

const API = API_BASE;

const ADMIN_EMAIL = 'mbourke@amity.nsw.edu.au';

const schoolNavItems = [
  { path: '/app/daily-overview', label: 'Today', icon: Sun },
  { path: '/app/dashboard', label: 'Classes', icon: LayoutDashboard },
  { path: '/app/live-mode', label: 'Teach Now', icon: Radio },
  { path: '/app/summaries', label: 'End of Lesson Summaries', icon: Sparkles },
  { path: '/app/timetable', label: 'Timetable', icon: Calendar },
  { path: '/app/school-info', label: 'School Info', icon: Info },
];

const teacherNavItems = [];

const bottomNavItems = [
  { path: '/app/daily-overview', label: 'Today', icon: Sun },
  { path: '/app/dashboard', label: 'Classes', icon: LayoutDashboard },
  { path: '/app/live-mode', label: 'Teach', icon: Radio },
  { path: '/app/summaries', label: 'Summaries', icon: Sparkles },
];

export const Layout = ({ children, title, breadcrumb }) => {
  const { user, logout } = useAuth();
  const { selectedSchool, clearSchool } = useSchool();
  const { preference, setPreference, isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const isBetaUser = trialStatus?.role === 'beta_user' || trialStatus?.status === 'beta';

  // Fetch trial status
  useEffect(() => {
    const fetchTrial = async () => {
      try {
        const r = await fetch(`${API}/auth/trial-status`, { credentials: 'include' });
        if (r.ok) setTrialStatus(await r.json());
      } catch (e) { /* ignore */ }
    };
    fetchTrial();
  }, []);

  const isClassRoute = CLASS_ROUTE_PREFIXES.some(p => location.pathname.startsWith(p));
  const darkActive = isDark && isClassRoute;

  // Apply/remove class-dark on <html> for portaled components (dialogs, popovers)
  useEffect(() => {
    if (darkActive) {
      document.documentElement.classList.add('class-dark');
    } else {
      document.documentElement.classList.remove('class-dark');
    }
    return () => document.documentElement.classList.remove('class-dark');
  }, [darkActive]);

  const handleLogout = async () => {
    clearSchool();
    await logout();
    navigate('/');
  };

  const handleChangeSchool = () => {
    clearSchool();
    navigate('/app/select-school');
  };

  return (
    <>
    <div className="min-h-screen bg-[#f8fafc]" data-testid="app-layout">
      {/* Top header bar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/80 h-16">
        <div className="h-full px-4 flex items-center justify-between">
          {/* Left side */}
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2 rounded-xl hover:bg-slate-100 transition-colors"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5 text-slate-600" />
            </button>
            
            <Link to="/app/dashboard" className="flex items-center">
              <img src="/logo.png" alt="Reliefify" className="w-14 h-14 object-contain" />
            </Link>
          </div>

          {/* Center - School selector */}
          {selectedSchool && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="school-selector">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">{selectedSchool.name}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                <DropdownMenuItem onClick={handleChangeSchool} className="cursor-pointer">
                  <Building2 className="w-4 h-4 mr-2" />
                  Change School
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Right side - User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 p-1 rounded-full hover:bg-slate-100 transition-colors" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8 ring-2 ring-slate-100">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-teal-600 text-white text-xs font-medium">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2.5 border-b border-slate-100">
                <p className="font-medium text-sm text-slate-800">{user?.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{user?.email}</p>
              </div>
              <DropdownMenuItem onClick={handleChangeSchool} className="cursor-pointer">
                <Building2 className="w-4 h-4 mr-2" />
                Change School
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-slate-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200/80 z-50 transform transition-transform duration-200 lg:translate-x-0 lg:top-16 lg:h-[calc(100vh-4rem)] ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-200">
            <div className="flex items-center">
              <img src="/logo.png" alt="Reliefify" className="w-14 h-14 object-contain" />
            </div>
            <button 
              className="p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {/* School section — only if school selected */}
            {selectedSchool && (
              <>
                <div className="flex items-center justify-between px-3 mb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{selectedSchool.name}</span>
                  <button
                    onClick={handleChangeSchool}
                    className="text-[10px] font-medium text-teal-500 hover:text-teal-600 transition-colors"
                    data-testid="change-school-sidebar"
                  >
                    Switch
                  </button>
                </div>
                {schoolNavItems.map((item) => {
                  const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        isActive 
                          ? 'bg-teal-50 text-teal-700' 
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className={`w-[18px] h-[18px] ${isActive ? 'text-teal-600' : ''}`} />
                      {item.label}
                    </Link>
                  );
                })}
                <div className="my-3 mx-3 border-t border-slate-100" />
              </>
            )}

            {/* Teacher section — always visible */}
            {teacherNavItems.filter(item => !(item.hideForBeta && isBetaUser)).map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive 
                      ? 'bg-teal-50 text-teal-700' 
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className={`w-[18px] h-[18px] ${isActive ? 'text-teal-600' : ''}`} />
                  {item.label}
                </Link>
              );
            })}

            {!selectedSchool && (
              <Link
                to="/app/select-school"
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-teal-600 hover:bg-teal-50 transition-colors"
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-select-school"
              >
                <Building2 className="w-[18px] h-[18px]" />
                Select a School
              </Link>
            )}

            <div className="my-3 mx-3 border-t border-slate-100" />

            {/* Feedback button */}
            <button
              onClick={() => { setFeedbackOpen(true); setSidebarOpen(false); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 w-full transition-colors"
              data-testid="nav-feedback"
            >
              <MessageSquare className="w-[18px] h-[18px]" /> Send Feedback
            </button>
            {/* Upgrade link — hidden for beta/pro users */}
            {trialStatus && !trialStatus.is_pro && !isBetaUser && (
              <Link
                to="/app/upgrade"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  location.pathname === '/app/upgrade'
                    ? 'bg-amber-50 text-amber-700'
                    : 'text-amber-600 hover:bg-amber-50 hover:text-amber-700'
                }`}
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-upgrade"
              >
                <Crown className="w-[18px] h-[18px]" />
                {trialStatus.trial_active ? 'Upgrade to Pro' : 'Subscribe Now'}
              </Link>
            )}
            {user?.email === ADMIN_EMAIL && (
              <Link
                to="/app/admin"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  location.pathname === '/app/admin'
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                }`}
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-admin"
              >
                <Shield className={`w-[18px] h-[18px] ${location.pathname === '/app/admin' ? 'text-white' : ''}`} />
                Admin
              </Link>
            )}
          </nav>

          {/* Contact, Theme & School info at bottom */}
          <div className="mt-auto">
            <div className="px-3 pb-2 space-y-1">
              {/* Theme toggle */}
              <div className="flex items-center gap-1 px-3 py-2" data-testid="theme-toggle">
                <span className="text-[10px] uppercase tracking-widest mr-auto" style={{ color: darkActive ? '#5c5f73' : undefined, ...(darkActive ? {} : {}) }}>
                  <span className={darkActive ? 'text-[#5c5f73]' : 'text-slate-400'}>Theme</span>
                </span>
                <button
                  onClick={() => setPreference('system')}
                  className={`p-1.5 rounded-lg transition-colors ${
                    preference === 'system'
                      ? (darkActive ? 'bg-[#262838] text-[#e2e4ec]' : 'bg-teal-100 text-teal-700')
                      : (darkActive ? 'text-[#5c5f73] hover:text-[#8b8ea0]' : 'text-slate-400 hover:text-slate-600')
                  }`}
                  title="System preference"
                  data-testid="theme-system-btn"
                >
                  <Monitor className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setPreference('light')}
                  className={`p-1.5 rounded-lg transition-colors ${
                    preference === 'light'
                      ? (darkActive ? 'bg-[#262838] text-[#e2e4ec]' : 'bg-teal-100 text-teal-700')
                      : (darkActive ? 'text-[#5c5f73] hover:text-[#8b8ea0]' : 'text-slate-400 hover:text-slate-600')
                  }`}
                  title="Light mode"
                  data-testid="theme-light-btn"
                >
                  <Sun className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setPreference('dark')}
                  className={`p-1.5 rounded-lg transition-colors ${
                    preference === 'dark'
                      ? (darkActive ? 'bg-[#262838] text-[#e2e4ec]' : 'bg-teal-100 text-teal-700')
                      : (darkActive ? 'text-[#5c5f73] hover:text-[#8b8ea0]' : 'text-slate-400 hover:text-slate-600')
                  }`}
                  title="Dark mode"
                  data-testid="theme-dark-btn"
                >
                  <Moon className="w-3.5 h-3.5" />
                </button>
              </div>
              {isClassRoute && (
                <p className={`px-3 text-[10px] ${darkActive ? 'text-[#5c5f73]' : 'text-slate-400'}`}>
                  {darkActive ? 'Dark mode active' : 'Dark mode available'}
                </p>
              )}
              <a
                href="mailto:reliefifycbm@gmail.com"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  darkActive
                    ? 'text-[#8b8ea0] hover:bg-[#1e2030] hover:text-[#c5c8d4]'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                }`}
                data-testid="nav-contact-support"
              >
                <Mail className="w-[18px] h-[18px]" />
                Contact Support
              </a>
            </div>
            {selectedSchool && (
              <div className={`p-4 border-t ${darkActive ? 'border-[#262838]' : 'border-slate-100'}`}>
                <div className={`text-[11px] uppercase tracking-widest mb-1 ${darkActive ? 'text-[#5c5f73]' : 'text-slate-400'}`}>Teaching at</div>
                <div className={`font-medium text-sm ${darkActive ? 'text-[#c5c8d4]' : 'text-slate-700'}`}>{selectedSchool.name}</div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64 pt-16">
        {/* Trial banner — hidden for beta users and pro users */}
        {trialStatus && !trialStatus.is_pro && !isBetaUser && (
          <div className={`px-4 py-4 ${
            trialStatus.trial_active
              ? 'bg-gradient-to-r from-teal-600 via-emerald-500 to-teal-600 text-white'
              : 'bg-gradient-to-r from-rose-600 via-orange-500 to-amber-500 text-white'
          }`} data-testid="trial-banner">
            {trialStatus.trial_active ? (
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="text-xs font-bold uppercase tracking-wider">
                      {trialStatus.days_left} day{trialStatus.days_left !== 1 ? 's' : ''} left
                    </span>
                  </div>
                </div>
                <p className="text-sm sm:text-base font-semibold text-center">
                  Your 14-day free trial is active — unlock all features forever for just $4.99/mo
                </p>
                <Link to="/app/upgrade">
                  <Button size="sm" className="bg-white text-teal-700 hover:bg-teal-50 rounded-full px-6 h-9 text-sm font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105">
                    <Crown className="w-4 h-4 mr-1.5" />
                    Upgrade to Pro
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-xs font-bold uppercase tracking-wider">Trial Expired</span>
                </div>
                <p className="text-sm sm:text-base font-semibold text-center">
                  Your free trial has ended — subscribe now to keep using Reliefify
                </p>
                <Link to="/app/upgrade">
                  <Button size="sm" className="bg-white text-orange-700 hover:bg-orange-50 rounded-full px-6 h-9 text-sm font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105 animate-pulse">
                    <Crown className="w-4 h-4 mr-1.5" />
                    Subscribe Now — $4.99/mo
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}
        <main className="p-5 lg:p-8 max-w-7xl mx-auto pb-24 lg:pb-8">
          {breadcrumb && breadcrumb.length > 0 && (
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => {
                  const parent = breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 2] : null;
                  if (parent?.path) {
                    navigate(parent.path);
                  } else {
                    navigate(-1);
                  }
                }}
                className="w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center transition-colors flex-shrink-0"
                data-testid="back-btn"
              >
                <ChevronLeft className="w-4 h-4 text-slate-500" />
              </button>
              <nav className="flex items-center gap-1.5 text-sm" data-testid="breadcrumb">
                {breadcrumb.map((item, i) => (
                  <span key={i} className="flex items-center gap-1.5">
                    {i > 0 && <span className="text-slate-300">/</span>}
                    {item.path ? (
                      <Link to={item.path} className="text-slate-500 hover:text-teal-600 transition-colors">{item.label}</Link>
                    ) : (
                      <span className="text-slate-800 font-medium">{item.label}</span>
                    )}
                  </span>
                ))}
              </nav>
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Trial Expired Paywall Overlay — blocks app when trial expired, except on upgrade page */}
      {trialStatus && !trialStatus.is_pro && !isBetaUser && !trialStatus.trial_active && location.pathname !== '/app/upgrade' && (
        <div className="fixed inset-0 z-[60] bg-white/95 backdrop-blur-sm flex items-center justify-center p-4" data-testid="trial-expired-overlay">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-50 mx-auto">
              <Crown className="w-8 h-8 text-amber-500" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold text-slate-900">Your trial has ended</h2>
              <p className="text-slate-500 mt-2 text-sm leading-relaxed">
                Your 14-day free trial of Reliefify has expired. Subscribe to Pro for just <strong className="text-slate-700">$4.99/month</strong> to continue managing your classrooms.
              </p>
            </div>
            <div className="space-y-3">
              <Link to="/app/upgrade" className="block">
                <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-12 text-base font-medium" data-testid="paywall-upgrade-btn">
                  <Crown className="w-4 h-4 mr-2" />
                  Subscribe Now — $4.99/mo
                </Button>
              </Link>
              <p className="text-xs text-slate-400">Cancel anytime. Your data is safe and will be here when you come back.</p>
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 lg:hidden" data-testid="bottom-nav">
        <div className="flex items-center justify-around h-16 px-2">
          {bottomNavItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center gap-1 flex-1 py-2 rounded-lg transition-colors ${
                  isActive ? 'text-teal-600' : 'text-slate-400'
                }`}
                data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className={`w-5 h-5 ${isActive ? 'text-teal-600' : 'text-slate-400'}`} />
                <span className="text-[10px] font-medium leading-none">{item.label}</span>
              </Link>
            );
          })}
          <button
            onClick={() => setSidebarOpen(true)}
            className="flex flex-col items-center justify-center gap-1 flex-1 py-2 rounded-lg text-slate-400"
            data-testid="bottom-nav-more"
          >
            <Menu className="w-5 h-5" />
            <span className="text-[10px] font-medium leading-none">More</span>
          </button>
        </div>
      </nav>
    </div>
    <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </>
  );
};
